import { compose, mapProps, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import type S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import { withFindOwnableProductsByUnderlyingProductItemId } from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import type { PropsFromFindByUnderlyingProductItemId } from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import { CREDENTIAL_TRACK_SUBSCRIPTION } from 'bundles/payments-common/common/ProductType';

export type PropsToWithPrice = {
  enrollmentAvailableChoices?: EnrollmentChoices;
  s12nId?: string;
};

type PropsFromWithPriceNaptime = {
  s12nDerivative?: S12nDerivativesV1;
  multiS12nDerivatives?: Array<S12nDerivativesV1>;
};

export type PropsFromWithPrice = PropsFromWithPriceNaptime;

type PropsTowithCredentialTrackOwnableProducts = {
  id: string;
};

export type OwnableProduct = {
  id: string;
  underlyingProductId?: string;
  productType: string;
  numberOfCycles?: number | null;
  subscriptionType?: string | null;
};

export type PropsFromWithCredentialTrackOwnableProducts = {
  ownableProducts?: OwnableProduct[];
};

type PropsFromWithProps = { underlyingProductItemId?: string };

const withCredentialTrackOwnableProducts = <InputProps extends PropsTowithCredentialTrackOwnableProducts>() =>
  compose<PropsFromWithCredentialTrackOwnableProducts, InputProps>(
    // This underlyingProductItemId is only used to get passed to `withFindOwnableProductsByUnderlyingProductItemId`
    withProps<PropsFromWithProps, InputProps>(({ id }) => {
      return { underlyingProductItemId: id };
    }),
    withFindOwnableProductsByUnderlyingProductItemId,
    mapProps<
      PropsFromWithCredentialTrackOwnableProducts,
      PropsFromWithProps & PropsFromFindByUnderlyingProductItemId
      // Destructure `underlyingProductItemId` so it doesn't returned back to the consuming component
    >(({ ownableProducts, underlyingProductItemId, ...props }) => {
      const formattedOwnableProducts = ownableProducts?.length
        ? ownableProducts?.map(({ productType, productItemId, numberOfInstallments, ...rest }) => {
            return {
              id: tupleToStringKey([productType ?? '', productItemId ?? '']),
              productType: productType ?? '',
              numberOfCycles: numberOfInstallments,
              underlyingProductId: tupleToStringKey([CREDENTIAL_TRACK_SUBSCRIPTION, underlyingProductItemId ?? '']),
              ...rest,
            };
          })
        : undefined;

      const sortedData = formattedOwnableProducts?.sort((skuA, skuB) => {
        // Need to set a number by default because 'properties' is possibly null/undefined
        const cyclesA = skuA?.numberOfCycles ?? 0;
        const cyclesB = skuB?.numberOfCycles ?? 0;
        return cyclesA - cyclesB;
      });

      return {
        ...props,
        ownableProducts: sortedData,
      };
    })
  );

export { withCredentialTrackOwnableProducts };
